import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import Accordion from '@mui/material/Accordion';
import { Button, TextField, Card, Grid, CardHeader, CardContent, IconButton, Tooltip } from '@mui/material';
import { SelectField } from 'components';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { enqueueSnackbar } from 'actions';
import ModalDialog from '../../../../../components/ModalDialog/ModalDialog.js';
import { SetFormState, GetIncidentDropdowns, ToggleOptionsIncidentTrackerForm, RemoveDropdownField } from '../../../actions/IncidentTrackerActions';
import IncidentTrackerOptionsForm from './components/components/IncidentTrackerOptionsForm';

const IncidentTrackerOptionsDialog = (props) => {
	const { author, open, onClose, onApply, className, ...rest } = props;
	const dispatch = useDispatch();
	const tableId = useSelector((state) => state.incidentTrackerDemoReducer.tableId);
	const userId = useSelector(state => state.session.user.id);
	const role = useSelector(state => state.session.user.role);
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector(state => state.incidentTrackerDemoReducer.formState);
	const dropdowns = useSelector(state => state.incidentTrackerDemoReducer.dropdowns);
	const optionsIncidentTrackerFormVisibility = useSelector(state => state.incidentTrackerDemoReducer.optionsIncidentTrackerFormVisibility);
	const dropdownLabel = useSelector(state => state.incidentTrackerDemoReducer.dropdownLabel);
	const dropdownName = useSelector(state => state.incidentTrackerDemoReducer.dropdownName);
	const dropdownValue = useSelector(state => state.incidentTrackerDemoReducer.dropdownValue);
	const dropdownId = useSelector(state => state.incidentTrackerDemoReducer.dropdownId);

	const newDropdownField = useSelector(state => state.incidentTrackerDemoReducer.newDropdownField);

	const [expanded, setExpanded] = useState(false);

	const handleChange = (panel) => (event, isExpanded) => {
		setExpanded(isExpanded ? panel : false);
	};

	const getData = useCallback(() => {
		dispatch(GetIncidentDropdowns());
	}, []);

	useEffect(() => {
		getData();
	}, []);

	const _handleChangeDropDown = event => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value
			},
			touched: {
				...formState.touched,
				[event.target.name]: true
			}
		});
	};

	const _handleChangeLevels = event => {
		if (event.target.name === 'levelOne') {
			// Filtrar
			let levelsTwo = dropdowns.levelsRelation
				.filter(x => x.levelOneId === event.target.value)
				.map(({ levelTwoId, levelTwoName }) => ({
					value: levelTwoId,
					text: levelTwoName
				}));
			// Distintos
			levelsTwo = [...new Map(levelsTwo.map((item) => [item.value, item])).values()];

			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]: event.target.value,
					levelTwo: undefined,
					levelThree: undefined,
					levelsTwo,
					levelsThree: [],
				},
			});
		} else if (event.target.name === 'levelTwo') {
			// Filtrar
			let levelsThree = dropdowns.levelsRelation
				.filter(x => x.levelTwoId === event.target.value)
				.map(({ levelThreeId, levelThreeName }) => ({
					value: levelThreeId,
					text: levelThreeName
				}));
			// Distintos
			levelsThree = [...new Map(levelsThree.map((item) => [item.value, item])).values()];

			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]: event.target.value,
					levelThree: undefined,
					levelsThree,
				},
			});
		} else if (event.target.name === 'levelThree') {
			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]: event.target.value
				},
			});
		}
	};

	const _addField = (event, label, name) => {
		dispatch(ToggleOptionsIncidentTrackerForm(true, label, name));
	};

	useMemo(() => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[newDropdownField.name]: newDropdownField.id
			}
		});
	}, [newDropdownField]);

	useMemo(() => {
		if (dropdowns && dropdowns.levelsRelation) {
			// Filtrar
			let levelsTwo = dropdowns.levelsRelation
				.filter(x => x.levelOneId === formState.values.levelOne)
				.map(({ levelTwoId, levelTwoName }) => ({
					value: levelTwoId,
					text: levelTwoName
				}));
			// Distintos
			levelsTwo = [...new Map(levelsTwo.map((item) => [item.value, item])).values()];

			// Filtrar
			let levelsThree = dropdowns.levelsRelation
				.filter(x => x.levelTwoId === formState.values.levelTwo)
				.map(({ levelThreeId, levelThreeName }) => ({
					value: levelThreeId,
					text: levelThreeName
				}));
			// Distintos
			levelsThree = [...new Map(levelsThree.map((item) => [item.value, item])).values()];

			setFormState({
				...formState,
				values: {
					...formState.values,
					levelsTwo,
					levelsThree
				}
			});
		}
	}, [dropdowns]);

	const _deleteField = (event, label, name, value, id) => {
		dispatch(ToggleOptionsIncidentTrackerForm(true, label, name, value, id));
	};

	return (
		<ModalDialog
			// maxWidth="xl"
			onClose={onClose}
			open={open}
			title="Options"
			// confirmLabel="Ok"
			hasConfirm={false}
			cancelLabel="Close"
		>
			<div
				style={{ marginTop: '10px' }}
			>
				{optionsIncidentTrackerFormVisibility && (
					<IncidentTrackerOptionsForm
						open={optionsIncidentTrackerFormVisibility}
						dropdownLabel={dropdownLabel}
						dropdownName={dropdownName}
						dropdownValue={dropdownValue}
						dropdownId={dropdownId}
					/>
				)}
				<Accordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel2bh-content"
						id="panel2bh-header"
					>
						<Typography sx={{ width: '33%', flexShrink: 0 }}>Edit Status</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={1}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="status"
									name="status"
									hintText="Status"
									defaultValue=""
									menuItems={(dropdowns && dropdowns.status) || []}
									value={formState.values.status || ''}
									onChange={_handleChangeDropDown}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<IconButton
										edge="end"
										size="small"
										style={{ color: '#1565c0' }}
										onClick={(e) => _addField(e, 'Status', 'status')}
									>
										<AddCircleOutlineIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title="Remove">
									<IconButton
										edge="end"
										size="small"
										style={{ color: formState.values.status === undefined ? 'grey' : 'red' }}
										disabled={formState.values.status === undefined}
										onClick={(e) => _deleteField(
											e,
											'Status',
											'status',
											dropdowns.status.find(x => x.value === formState.values.status).text,
											formState.values.status
										)}
									>
										<CancelIcon />
									</IconButton>
								</Tooltip>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion expanded={expanded === 'panel4'} onChange={handleChange('panel4')}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel4bh-content"
						id="panel4bh-header"
					>
						<Typography sx={{ width: '33%', flexShrink: 0 }}>Edit Solution Type</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={1}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="solutionType"
									name="solutionType"
									hintText="Solution Type"
									defaultValue=""
									menuItems={(dropdowns && dropdowns.solutionType) || []}
									value={formState.values.solutionType || ''}
									onChange={_handleChangeDropDown}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<IconButton
										edge="end"
										size="small"
										style={{ color: '#1565c0' }}
										onClick={(e) => _addField(e, 'Solution Type', 'solutionType')}
									>
										<AddCircleOutlineIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title="Remove">
									<IconButton
										edge="end"
										size="small"
										style={{ color: formState.values.solutionType === undefined ? 'grey' : 'red' }}
										disabled={formState.values.solutionType === undefined}
										onClick={(e) => _deleteField(
											e,
											'Solution Type',
											'solutionType',
											dropdowns.solutionType.find(x => x.value === formState.values.solutionType).text,
											formState.values.solutionType
										)}
									>
										<CancelIcon />
									</IconButton>
								</Tooltip>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
				<Accordion expanded={expanded === 'panel5'} onChange={handleChange('panel5')}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel5bh-content"
						id="panel5bh-header"
					>
						<Typography sx={{ width: '33%', flexShrink: 0 }}>Edit Levels</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={1} style={{ marginTop: '10px' }}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="levelOne"
									name="levelOne"
									hintText="Level One"
									defaultValue=""
									menuItems={(dropdowns && dropdowns.levelOne) || []}
									value={formState.values.levelOne || ''}
									onChange={_handleChangeLevels}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<IconButton
										edge="end"
										size="small"
										style={{ color: '#1565c0' }}
										onClick={(e) => _addField(e, 'Level One', 'levelOne')}
									>
										<AddCircleOutlineIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title="Remove">
									<IconButton
										edge="end"
										size="small"
										style={{ color: formState.values.levelOne === undefined ? 'grey' : 'red' }}
										disabled={formState.values.levelOne === undefined}
										onClick={(e) => _deleteField(
											e,
											'Level One',
											'levelOne',
											dropdowns.levelOne.find(x => x.value === formState.values.levelOne).text,
											formState.values.levelOne
										)}
									>
										<CancelIcon />
									</IconButton>
								</Tooltip>
							</Grid>
						</Grid>
						<Grid container spacing={1} style={{ marginTop: '10px' }}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="levelTwo"
									name="levelTwo"
									hintText="Level Two"
									defaultValue=""
									menuItems={formState.values.levelsTwo || []}
									value={formState.values.levelTwo || ''}
									onChange={_handleChangeLevels}
									disabled={formState.values.levelOne === undefined}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<IconButton
										edge="end"
										size="small"
										style={{ color: '#1565c0' }}
										onClick={(e) => _addField(e, 'Level Two', 'levelTwo')}
									>
										<AddCircleOutlineIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title="Remove">
									<IconButton
										edge="end"
										size="small"
										style={{ color: formState.values.levelTwo === undefined ? 'grey' : 'red' }}
										disabled={formState.values.levelTwo === undefined}
										onClick={(e) => _deleteField(
											e,
											'Level Two',
											'levelTwo',
											dropdowns.levelTwo.find(x => x.value === formState.values.levelTwo).text,
											formState.values.levelTwo
										)}
									>
										<CancelIcon />
									</IconButton>
								</Tooltip>
							</Grid>
						</Grid>
						<Grid container spacing={1} style={{ marginTop: '10px' }}>
							<Grid item md={3} xs={3}>
								<SelectField
									id="levelThree"
									name="levelThree"
									hintText="Level Three"
									defaultValue=""
									menuItems={formState.values.levelsThree || []}
									value={formState.values.levelThree || ''}
									onChange={_handleChangeLevels}
									disabled={formState.values.levelOne === undefined || formState.values.levelTwo === undefined}
								/>
							</Grid>
							<Grid item md={3} xs={3} style={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title="Add">
									<IconButton
										edge="end"
										size="small"
										style={{ color: formState.values.levelTwo === undefined ? 'grey' : '#1565c0' }}
										disabled={formState.values.levelTwo === undefined}
										onClick={(e) => _addField(e, 'Level Three', 'levelThree')}
									>
										<AddCircleOutlineIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title="Remove">
									<IconButton
										edge="end"
										size="small"
										style={{ color: formState.values.levelThree === undefined ? 'grey' : 'red' }}
										disabled={formState.values.levelThree === undefined}
										onClick={(e) => _deleteField(
											e,
											'Level Three',
											'levelThree',
											dropdowns.levelThree.find(x => x.value === formState.values.levelThree).text,
											formState.values.levelThree
										)}

									>
										<CancelIcon />
									</IconButton>
								</Tooltip>
							</Grid>
						</Grid>
					</AccordionDetails>
				</Accordion>
			</div>
		</ModalDialog>
	);
};

IncidentTrackerOptionsDialog.propTypes = {
	author: PropTypes.object.isRequired,
	className: PropTypes.string,
	onApply: PropTypes.func,
	onClose: PropTypes.func,
	open: PropTypes.bool.isRequired,
};

export default IncidentTrackerOptionsDialog;
